import { createAuthRequestInit } from '../utils/auth';
import { API_BASE } from '../config/api';

export interface SmsLogEntry {
  recipient_id: string;
  message_id: string;
  phone: string;
  status: 'queued' | 'sent' | 'delivered' | 'failed';
  provider_message_id: string | null;
  error: string | null;
  recipient_created_at: string;
  recipient_updated_at: string;
  sender_id: string | null;
  username?: string;
  body: string;
  char_count: number;
  parts: number;
  message_created_at: string;
}

export interface SmsLogsFilters {
  status?: 'queued' | 'sent' | 'delivered' | 'failed';
  phone?: string;
  senderId?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  updatedSince?: string;
  q?: string; // search query for message body
  page?: number;
  limit?: number;
}

export interface SmsLogsResponse {
  data: SmsLogEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

export interface SmsLogsStats {
  total: number;
  delivered: number;
  failed: number;
  pending: number;
  sent: number;
  deliveryRate: number;
}

// Build query string from filters
function buildQueryString(filters: SmsLogsFilters): string {
  const params = new URLSearchParams();
  
  if (filters.status) params.append('status', filters.status);
  if (filters.phone) params.append('phone', filters.phone);
  if (filters.senderId) params.append('senderId', filters.senderId);
  if (filters.userId) params.append('userId', filters.userId);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.startTime) params.append('startTime', filters.startTime);
  if (filters.endTime) params.append('endTime', filters.endTime);
  if (filters.updatedSince) params.append('updatedSince', filters.updatedSince);
  if (filters.q) params.append('q', filters.q);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));
  
  return params.toString();
}

/**
 * Fetch SMS logs with optional filters and pagination
 */
export async function fetchSmsLogs(filters: SmsLogsFilters = {}): Promise<SmsLogsResponse> {
  const queryString = buildQueryString(filters);
  const url = `${API_BASE}/sms/logs${queryString ? `?${queryString}` : ''}`;
  
  const res = await fetch(url, createAuthRequestInit());
  
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to fetch SMS logs');
  }
  
  const json = await res.json();
  return json as SmsLogsResponse;
}

/**
 * Fetch SMS logs updated since a specific timestamp (for real-time updates)
 */
export async function fetchSmsLogsRealTime(updatedSince: string, filters: Omit<SmsLogsFilters, 'updatedSince'> = {}): Promise<SmsLogsResponse> {
  return fetchSmsLogs({ ...filters, updatedSince });
}

/**
 * Fetch SMS logs statistics for the last 24 hours for a specific user
 */
export async function fetchSmsLogsStats(userId?: string): Promise<SmsLogsStats> {
  const params = new URLSearchParams();
  if (userId) params.append('userId', userId);
  
  const url = `${API_BASE}/sms/logs/stats${params.toString() ? `?${params.toString()}` : ''}`;
  
  const res = await fetch(url, createAuthRequestInit());
  
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to fetch SMS logs statistics');
  }
  
  const json = await res.json();
  return json.data as SmsLogsStats;
}

/**
 * Calculate statistics from logs data
 */
export function calculateStats(logs: SmsLogEntry[]): SmsLogsStats {
  const total = logs.length;
  const delivered = logs.filter(log => log.status === 'delivered').length;
  const failed = logs.filter(log => log.status === 'failed').length;
  const pending = logs.filter(log => log.status === 'queued').length;
  const sent = logs.filter(log => log.status === 'sent').length;
  
  const deliveryRate = total > 0 ? (delivered / total) * 100 : 0;
  
  return {
    total,
    delivered,
    failed,
    pending,
    sent,
    deliveryRate: Math.round(deliveryRate * 100) / 100 // Round to 2 decimal places
  };
}

/**
 * Export logs to CSV format
 */
export function exportLogsToCSV(logs: SmsLogEntry[]): string {
  const headers = [
    'Message ID',
    'Recipient ID',
    'Phone',
    'Status',
    'Sender ID',
    'Username',
    'Message Body',
    'Char Count',
    'Parts',
    'Sent Time',
    'Delivered Time',
    'Failed Time',
    'Error Message',
    'Provider Message ID'
  ];
  
  const rows = logs.map(log => {
    const sentTime = log.status !== 'queued' ? new Date(log.recipient_created_at).toISOString() : '';
    const deliveredTime = log.status === 'delivered' ? new Date(log.recipient_updated_at).toISOString() : '';
    const failedTime = log.status === 'failed' ? new Date(log.recipient_updated_at).toISOString() : '';
    
    return [
      log.message_id,
      log.recipient_id,
      log.phone,
      log.status,
      log.sender_id || '',
      log.username || '',
      `"${log.body.replace(/"/g, '""')}"`, // Escape quotes in CSV
      String(log.char_count),
      String(log.parts),
      sentTime,
      deliveredTime,
      failedTime,
      log.error ? `"${log.error.replace(/"/g, '""')}"` : '',
      log.provider_message_id || ''
    ];
  });
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
  
  return csvContent;
}

/**
 * Download logs as CSV file
 */
export function downloadLogsAsCSV(logs: SmsLogEntry[], filename?: string): void {
  const csvContent = exportLogsToCSV(logs);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename || `sms-logs-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}

// Company users for admin filter
export interface CompanyUserOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  company?: string;
}

export async function fetchCompanyUsers(): Promise<CompanyUserOption[]> {
  const res = await fetch(`${API_BASE}/users/company`, createAuthRequestInit());
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to fetch company users');
  }
  const json = await res.json();
  return (json?.data?.users || []) as CompanyUserOption[];
}

export async function fetchAllUsers(params?: { q?: string; page?: number; limit?: number }): Promise<{ users: CompanyUserOption[]; pagination: { page: number; limit: number; total: number } }> {
  const search = new URLSearchParams();
  if (params?.q) search.append('q', params.q);
  if (params?.page) search.append('page', String(params.page));
  if (params?.limit) search.append('limit', String(params.limit));
  const url = `${API_BASE}/users/all${search.toString() ? `?${search.toString()}` : ''}`;
  const res = await fetch(url, createAuthRequestInit());
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to fetch users');
  }
  const json = await res.json();
  return { users: (json?.data?.users || []) as CompanyUserOption[], pagination: json?.data?.pagination };
}

